import { useEffect, useState, createContext, useContext } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

// ------------------------------------------------------------------
// Editorial navigation architecture
// Sections are dropdown groups; a section with a `to` also acts as a link.
// ------------------------------------------------------------------
export const NAV_SECTIONS = [
  {
    label: "Work",
    to: "/work",
    items: [
      { to: "/work", label: "Selected Work" },
      { to: "/films", label: "Films" },
      { to: "/work/photography", label: "Photography" },
      { to: "/work/campaigns", label: "Campaigns" },
    ],
  },
  {
    label: "Impact",
    to: "/impact",
    items: [
      { to: "/impact", label: "Impact Overview" },
      { to: "/impact/ditch-the-screens", label: "Ditch the Screens · Scouts Canada" },
      { to: "/impact/scout-popcorn", label: "Scout Popcorn · Scouts Canada" },
      { to: "/impact/scouts-volunteer-recruitment", label: "Volunteer Recruitment · Scouts Canada" },
      { to: "/impact/chipmunks", label: "Chipmunks Early Years · Scouts Canada" },
      { to: "/impact/raise-chess-giving-tuesday", label: "Giving Tuesday · Raise Chess Academy" },
    ],
  },
  {
    label: "Nocturne",
    to: "/nocturne",
    items: [
      { to: "/nocturne", label: "Nocturne" },
      { to: "/films", label: "Films" },
      { to: "/journal", label: "Journal" },
      { to: "/garage", label: "The Garage" },
    ],
  },
  {
    label: "Studio",
    to: "/about",
    items: [
      { to: "/about", label: "About" },
      { to: "/services", label: "Services" },
    ],
  },
  { label: "Contact", to: "/contact", items: null },
];

export const MagazineModalContext = createContext({ open: () => {} });
export const useMagazineModal = () => useContext(MagazineModalContext);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const loc = useLocation();
  const { open } = useMagazineModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll + close on route change already handled below.
  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobile]);


  useEffect(() => {
    setMobile(false);
    setOpenSection(null);
  }, [loc.pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobile ? "bg-black/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-2">
          <span className="font-display text-2xl tracking-tight text-white">Good Bandit</span>
          <span className="kbd hidden md:inline">/ THE GOOD BANDIT</span>
        </Link>

        {/* DESKTOP NAV */}
        <nav
          className="hidden lg:flex items-center gap-10"
          onMouseLeave={() => setOpenSection(null)}
        >
          {NAV_SECTIONS.map((section) => {
            const active =
              (section.to && loc.pathname === section.to) ||
              (section.items && section.items.some((i) => i.to === loc.pathname));
            const isOpen = openSection === section.label;
            return (
              <div
                key={section.label}
                className="relative"
                onMouseEnter={() => section.items && setOpenSection(section.label)}
              >
                <NavLink
                  to={section.to || "#"}
                  data-testid={`nav-${section.label.toLowerCase()}`}
                  className={`text-[11px] tracking-[0.25em] uppercase font-medium gb-link ${
                    active ? "text-[var(--gb-gold)]" : "text-white/85"
                  }`}
                  end={section.to === "/"}
                >
                  {section.label}
                </NavLink>

                {/* Editorial dropdown */}
                <AnimatePresence>
                  {isOpen && section.items && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50 min-w-[280px]"
                    >
                      <div className="bg-black/95 backdrop-blur-xl border border-white/10 py-4">
                        {section.items.map((item, idx) => (
                          <NavLink
                            key={item.to + idx}
                            to={item.to}
                            data-testid={`dropdown-${section.label.toLowerCase()}-${idx}`}
                            className={({ isActive }) =>
                              `block px-6 py-2.5 text-[12px] tracking-[0.15em] uppercase transition-colors duration-300 whitespace-nowrap ${
                                isActive
                                  ? "text-[var(--gb-gold)]"
                                  : "text-white/70 hover:text-[var(--gb-gold)]"
                              }`
                            }
                          >
                            {item.label}
                          </NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-subscribe-button"
            onClick={() => open("nav")}
            className="hidden lg:inline-flex gb-btn-primary text-[10px] py-3 px-5"
          >
            Download Latest Magazine
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2 z-[60]"
            onClick={() => setMobile((v) => !v)}
            aria-label="menu"
          >
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* MOBILE FULL-SCREEN NAV */}
      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden fixed left-0 right-0 top-20 bottom-0 h-[calc(100vh-5rem)] bg-black overflow-y-auto z-[55]"
            data-testid="mobile-nav-panel"
          >
            <div className="min-h-full px-6 py-12 flex flex-col justify-between">
              <div className="space-y-8">
                {NAV_SECTIONS.map((section, i) => (
                  <motion.div
                    key={section.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + i * 0.06, duration: 0.6 }}
                    className="border-b border-white/5 pb-6"
                  >
                    <NavLink
                      to={section.to || "#"}
                      onClick={() => section.to && setMobile(false)}
                      data-testid={`mobile-nav-${section.label.toLowerCase()}`}
                      className="font-display text-4xl text-white block leading-[1]"
                    >
                      {section.label}
                    </NavLink>
                    {section.items && section.items.length > 1 && (
                      <div className="mt-4 space-y-2 pl-1">
                        {section.items.map((item, j) => (
                          <NavLink
                            key={item.to + j}
                            to={item.to}
                            onClick={() => setMobile(false)}
                            className="block text-[11px] tracking-[0.2em] uppercase text-white/50 py-1.5"
                          >
                            {item.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              <div className="mt-12 pb-24">
                <button
                  data-testid="mobile-subscribe-button"
                  onClick={() => { setMobile(false); open("nav-mobile"); }}
                  className="gb-btn-primary w-full justify-center"
                >
                  Download Latest Magazine
                </button>
                <p className="kbd text-white/30 mt-8 text-center">
                  © 2026 GOOD BANDIT · EDITORIAL EXPERIENCES
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
